import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { API } from '../App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Sparkles, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const PracticeTestGenerating = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const timer = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/practice-tests/jobs/${jobId}`);
        if (cancelled) return;
        setJob(data);
        if (data.status === 'ready' || data.status === 'failed') {
          clearInterval(timer.current);
          if (data.status === 'ready') {
            axios.post(`${API}/practice-tests/jobs/${jobId}/ack`).catch(() => {});
          }
        }
      } catch (e) {
        clearInterval(timer.current);
        toast.error('Could not track this generation job');
      }
    };

    poll();
    timer.current = setInterval(poll, 3000);
    return () => {
      cancelled = true;
      clearInterval(timer.current);
    };
  }, [jobId]);

  const status = job?.status || 'queued';
  const progress = job?.progress ?? 5;

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="practice-generating-page">
      <Card className="border-0 shadow-xl overflow-hidden">
        <div className="h-1.5 bg-gradient-to-r from-violet-600 via-indigo-500 to-sky-400" />
        <CardContent className="p-8 text-center space-y-6">
          {status === 'failed' ? (
            <AlertTriangle className="w-12 h-12 text-rose-500 mx-auto" />
          ) : status === 'ready' ? (
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" data-testid="generation-ready-icon" />
          ) : (
            <div className="relative w-16 h-16 mx-auto">
              <Loader2 className="w-16 h-16 text-violet-200 animate-spin absolute inset-0" />
              <Sparkles className="w-7 h-7 text-violet-600 absolute inset-0 m-auto" />
            </div>
          )}

          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2" data-testid="generation-title">
              {status === 'ready'
                ? 'Your test is ready'
                : status === 'failed'
                ? 'Generation failed'
                : 'Building your practice test'}
            </h1>
            <p className="text-base text-slate-600" data-testid="generation-message">
              {job?.message || 'Queued — warming up the question engine...'}
            </p>
          </div>

          {status !== 'failed' && (
            <div className="space-y-2">
              <Progress value={progress} className="h-2" data-testid="generation-progress-bar" />
              <div className="text-sm text-slate-500" data-testid="generation-progress-value">{progress}%</div>
            </div>
          )}

          {job && (
            <div className="text-sm text-slate-500" data-testid="generation-meta">
              {job.exam} • {job.mode === 'full' ? 'Full length' : `Part test${job.subject ? ` · ${job.subject}` : ''}`}
              {job.question_count ? ` • ${job.question_count} questions` : ''}
            </div>
          )}

          {status === 'ready' ? (
            <Button
              className="bg-violet-600 hover:bg-violet-700 text-white rounded-full h-12 px-8 font-semibold"
              onClick={() => navigate(`/practice/${job.test_id}`)}
              data-testid="generation-start-test-button"
            >
              Start test
            </Button>
          ) : status === 'failed' ? (
            <Button asChild className="rounded-full h-12 px-8" data-testid="generation-retry-button">
              <Link to="/practice">Try again</Link>
            </Button>
          ) : (
            <div className="rounded-xl bg-violet-50 border border-violet-100 p-4 text-sm text-violet-800">
              You can leave this page — we keep generating in the background and will notify you the moment it&apos;s
              ready.
              <div className="mt-3 flex justify-center gap-3">
                <Button asChild variant="outline" size="sm" className="rounded-full" data-testid="generation-leave-button">
                  <Link to="/">Back to dashboard</Link>
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PracticeTestGenerating;
